import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { TicketKey } from '@/constants/tickets'
import type { SessionDetail } from '@/lib/server/shows'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowLeft, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { bestAvailable, describeSeats, SELLABLE_SEATS } from '@/lib/seats'
import { formatLongDay, formatTime } from '@/lib/time'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Media } from '@/components/generals/media'
import { FadeLink } from '@/components/generals/fade-link'
import { SeatLegend, SeatMap } from '@/components/shows/seat-map'
import { getSession, getTakenSeats } from '@/lib/server/shows'
import { releaseShowHold } from '@/lib/server/show-bookings'
import { paymentsEnabled } from '@/lib/server/payments'

import {
    Checkbox,
    Choice,
    Notice,
    Stepper,
    TextField
} from '@/components/generals/form'

import {
    MAX_SEATS_PER_BOOKING,
    TICKET_TYPES,
    formatPrice,
    seatsFor,
    totalFor
} from '@/constants/tickets'

type Props = {
    session: SessionDetail | null
    taken: string[]
    cardPayments: boolean
    closed: boolean
    released: boolean
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const BookSession: FC<Props> = ({ session, taken: initialTaken, cardPayments, closed, released }): ReactNode => {
    const router = useRouter()
    const [tickets, setTickets] = useState<Record<TicketKey, number>>({ adult: 2, child: 0, student: 0, member: 0, family: 0 })
    const [seats, setSeats] = useState<string[]>([])
    const [taken, setTaken] = useState<Set<string>>(() => new Set(initialTaken))
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [newsletter, setNewsletter] = useState(false)
    const [payment, setPayment] = useState<'card' | 'on_arrival'>(cardPayments ? 'card' : 'on_arrival')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [sending, setSending] = useState(false)
    const [message, setMessage] = useState<{ tone: 'error' | 'info', text: string } | null>(released ? { tone: 'info', text: 'Your seats were released and nothing was charged. You can choose again below.' } : null)

    const needed = seatsFor(tickets)
    const total = totalFor(tickets)
    const available = SELLABLE_SEATS - taken.size

    useEffect(() => {
        setSeats(current => (current.length > needed ? current.slice(current.length - needed) : current))
    }, [needed])

    useEffect(() => {
        if (!session || closed) return

        const refresh = async () => {
            try {
                const response = await fetch(`/api/sessions/${session.id}/seats`)
                const data = await response.json() as { taken?: string[] }

                if (!response.ok || !Array.isArray(data.taken)) return

                const next = new Set(data.taken)

                setTaken(next)
                setSeats(current => {
                    const kept = current.filter(seat => !next.has(seat))

                    if (kept.length !== current.length) {
                        setMessage({ tone: 'error', text: 'Someone has just booked one of the seats you chose. Please pick another.' })
                    }

                    return kept
                })
            }
            catch {
                // The map stays as it is; the server checks again on booking.
            }
        }

        const timer = window.setInterval(refresh, 20000)

        return () => window.clearInterval(timer)
    }, [session, closed])

    const start = useMemo(() => (session ? new Date(session.startsAt) : null), [session])

    if (!session || !start) {
        return (
            <Layout>
                <Seo title='Book a show' noIndex />
                <section className='container-page flex min-h-[80svh] flex-col justify-center py-40'>
                    <p className='label-mono text-brass'>
                        Bookings
                    </p>
                    <h1 className='display-serif mt-5 text-5xl text-star md:text-7xl'>
                        We could not load this show just now.
                    </h1>
                    <p className='mt-6 max-w-lg text-star-2'>
                        The booking system did not answer. Please try again in a moment, or choose another time.
                    </p>
                    <div className='mt-10 flex flex-wrap gap-4'>
                        <button type='button' onClick={() => router.reload()} className={buttonClass('primary')}>
                            Try again
                        </button>
                        <FadeLink href='/shows' className={buttonClass('outline')}>
                            All show times
                        </FadeLink>
                    </div>
                </section>
            </Layout>
        )
    }

    const toggleSeat = (id: string) => {
        setMessage(null)
        setErrors(current => ({ ...current, seats: '' }))
        setSeats(current => {
            if (current.includes(id)) return current.filter(seat => seat !== id)
            if (needed === 0) return current
            if (current.length < needed) return [...current, id]

            return [...current.slice(1), id]
        })
    }

    const chooseBest = () => {
        const best = bestAvailable(taken, needed)

        setSeats(best)
        setMessage(best.length < needed ? { tone: 'error', text: 'There are not enough seats left together. Pick seats on the plan.' } : null)
    }

    const setTicket = (key: TicketKey, value: number) => {
        setTickets(current => {
            const next = { ...current, [key]: value }

            return seatsFor(next) > MAX_SEATS_PER_BOOKING ? current : next
        })
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const nextErrors: Record<string, string> = {}

        if (needed === 0) nextErrors.tickets = 'Choose at least one ticket.'
        if (seats.length !== needed) nextErrors.seats = `Choose ${needed} seat${needed === 1 ? '' : 's'} on the plan, or use Pick the best seats.`
        if (name.trim().length < 2) nextErrors.name = 'Please tell us your name.'
        if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = 'Please check your email address.'

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) {
            setMessage({ tone: 'error', text: 'Please check the highlighted details.' })
            return
        }

        setSending(true)
        setMessage(null)

        try {
            const response = await fetch('/api/bookings/show', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId: session.id, seats, tickets, name, email, phone, newsletter, payment })
            })
            const data = await response.json().catch(() => null) as { redirectUrl?: string, message?: string, errors?: Record<string, string> } | null

            if (!response.ok || !data?.redirectUrl) {
                if (data?.errors) setErrors(data.errors)
                setMessage({ tone: 'error', text: data?.message ?? 'Something went wrong. Nothing has been charged.' })

                if (response.status === 409) {
                    const refreshed = await fetch(`/api/sessions/${session.id}/seats`).then(result => result.json()).catch(() => null) as { taken?: string[] } | null

                    if (refreshed?.taken) {
                        const next = new Set(refreshed.taken)

                        setTaken(next)
                        setSeats(current => current.filter(seat => !next.has(seat)))
                    }
                }

                setSending(false)
                return
            }

            if (data.redirectUrl.startsWith('http')) window.location.assign(data.redirectUrl)
            else void router.push(data.redirectUrl)
        }
        catch {
            setMessage({ tone: 'error', text: 'We could not reach the booking system. Nothing has been charged. Please try again.' })
            setSending(false)
        }
    }

    const when = `${formatLongDay(start)} at ${formatTime(start)}`

    return (
        <Layout>
            <Seo title={`Book ${session.showTitle}`} description={`Choose your seats under the dome for ${session.showTitle}, ${when}.`} noIndex />

            <section className='container-page pb-10 pt-28 md:pt-36'>
                <FadeLink href='/shows' className='label-mono inline-flex items-center gap-2 text-star-3 transition-colors hover:text-star'>
                    <ArrowLeft aria-hidden className='size-3.5' strokeWidth={1.25} />
                    All show times
                </FadeLink>

                <div className='mt-8 grid gap-8 md:grid-cols-[auto_1fr] md:items-end'>
                    <Media src={session.show.poster ?? '/images/planetarium-theatre.jpg'} alt={session.show.posterAlt} sizes='160px' className='aspect-[3/4] w-28 md:w-36' />
                    <div>
                        <p className='label-mono text-brass'>
                            {`${session.kind} · ${session.duration} minutes · ${session.ageLabel}`}
                        </p>
                        <h1 className='display-serif mt-3 text-5xl text-star md:text-7xl'>
                            {session.showTitle}
                        </h1>
                        <p className='data-mono mt-4 text-lg text-star-2'>
                            {when}
                        </p>
                    </div>
                </div>
            </section>

            {closed ? (
                <section className='container-page pb-32'>
                    <Notice tone='info'>
                        Online booking has closed for this performance. Any last seats are sold at the desk, or choose another time.
                    </Notice>
                    <FadeLink href='/shows' className={buttonClass('primary', 'mt-8')}>
                        See other times
                    </FadeLink>
                </section>
            ) : (
                <form onSubmit={handleSubmit} noValidate className='container-page grid gap-14 pb-32 lg:grid-cols-12'>
                    <div className='lg:col-span-7'>
                        <fieldset>
                            <legend className='flex w-full items-baseline justify-between gap-4 border-b border-hair pb-4'>
                                <span className='font-heading text-3xl text-star'>
                                    1. Tickets
                                </span>
                                <span className='label-mono text-star-3'>
                                    {`${available} of ${SELLABLE_SEATS} seats left`}
                                </span>
                            </legend>
                            <ul>
                                {TICKET_TYPES.map(type => (
                                    <li key={type.key} className='flex items-center justify-between gap-4 border-b border-hair py-4'>
                                        <div>
                                            <p className='text-lg text-star'>
                                                {type.label}
                                                <span className='data-mono ml-3 text-star-2'>
                                                    {formatPrice(type.pence)}
                                                </span>
                                            </p>
                                            <p className='text-sm text-star-3'>
                                                {type.detail}
                                            </p>
                                        </div>
                                        <Stepper label={type.label} value={tickets[type.key]} onChange={value => setTicket(type.key, value)} max={type.seats > 1 ? 3 : MAX_SEATS_PER_BOOKING} />
                                    </li>
                                ))}
                            </ul>
                            <p className='mt-3 text-sm text-star-3'>
                                Children under four go free on a grown-up&apos;s lap: no ticket needed. Up to twelve seats per booking.
                            </p>
                            {errors.tickets && (
                                <p className='mt-2 text-sm text-signal'>
                                    {errors.tickets}
                                </p>
                            )}
                        </fieldset>

                        <fieldset className='mt-16'>
                            <legend className='flex w-full flex-wrap items-baseline justify-between gap-4 border-b border-hair pb-4'>
                                <span className='font-heading text-3xl text-star'>
                                    2. Seats
                                </span>
                                <span className='label-mono text-star-3' aria-live='polite'>
                                    {`${seats.length} of ${needed} chosen`}
                                </span>
                            </legend>
                            <p className='mt-4 text-star-2'>
                                The seats recline under the dome, so every seat looks up at the whole sky. The middle rings give the most comfortable view.
                            </p>
                            <div className='mt-6 flex flex-wrap items-center gap-4'>
                                <button type='button' onClick={chooseBest} disabled={needed === 0} className={buttonClass('quiet')}>
                                    <Sparkles aria-hidden className='size-3.5' strokeWidth={1.25} />
                                    Pick the best seats for me
                                </button>
                                {seats.length > 0 && (
                                    <button type='button' onClick={() => setSeats([])} className='label-mono text-star-3 hover:text-star'>
                                        Clear
                                    </button>
                                )}
                            </div>
                            <div className='mt-8'>
                                <SeatMap taken={taken} selected={seats} onToggle={toggleSeat} />
                            </div>
                            <div className='mt-6'>
                                <SeatLegend />
                            </div>
                            {errors.seats && (
                                <p className='mt-4 text-sm text-signal'>
                                    {errors.seats}
                                </p>
                            )}
                            <p className='mt-4 text-sm text-star-3'>
                                Wheelchair spaces are next to the entrance and have a companion seat beside them. If you need anything else, tell us in a note at the desk or write to us before your visit.
                            </p>
                        </fieldset>

                        <fieldset className='mt-16'>
                            <legend className='w-full border-b border-hair pb-4 font-heading text-3xl text-star'>
                                3. Your details
                            </legend>
                            <div className='mt-6 grid gap-8 sm:grid-cols-2'>
                                <TextField id='booking-name' label='Full name' value={name} onChange={setName} autoComplete='name' required error={errors.name} />
                                <TextField id='booking-email' label='Email' type='email' inputMode='email' value={email} onChange={setEmail} autoComplete='email' required error={errors.email} hint='Your tickets will be sent here.' />
                                <TextField id='booking-phone' label='Phone' type='tel' inputMode='tel' value={phone} onChange={setPhone} autoComplete='tel' hint='Only used if we need to reach you about this show.' />
                            </div>
                            <Checkbox
                                id='booking-newsletter'
                                className='mt-8'
                                checked={newsletter}
                                onChange={setNewsletter}
                                label='Send me Clear skies, the monthly sky guide'
                                description='One email a month. Unsubscribe whenever you like.'
                            />
                        </fieldset>

                        <fieldset className='mt-16'>
                            <legend className='w-full border-b border-hair pb-4 font-heading text-3xl text-star'>
                                4. Payment
                            </legend>
                            <div className='mt-6 space-y-3'>
                                {cardPayments && (
                                    <Choice
                                        name='payment'
                                        value='card'
                                        checked={payment === 'card'}
                                        onChange={() => setPayment('card')}
                                        title='Pay now by card'
                                        description='You will finish on a secure payment page and come straight back to your ticket.'
                                    />
                                )}
                                <Choice
                                    name='payment'
                                    value='on_arrival'
                                    checked={payment === 'on_arrival'}
                                    onChange={() => setPayment('on_arrival')}
                                    title='Reserve now, pay at the desk'
                                    description='Your seats are held. Pay by card or cash when you arrive, at least fifteen minutes before the show.'
                                />
                            </div>
                        </fieldset>
                    </div>

                    <aside className='lg:col-span-5'>
                        <div className='border border-hair bg-ink-2/70 p-6 lg:sticky lg:top-24 md:p-8'>
                            <p className='label-mono text-brass'>
                                Your booking
                            </p>
                            <p className='mt-3 font-heading text-3xl text-star'>
                                {session.showTitle}
                            </p>
                            <p className='data-mono mt-2 text-star-2'>
                                {when}
                            </p>

                            <dl className='mt-6 border-t border-hair'>
                                {TICKET_TYPES.filter(type => tickets[type.key] > 0).map(type => (
                                    <div key={type.key} className='flex justify-between gap-4 border-b border-hair py-3'>
                                        <dt className='text-star-2'>
                                            {`${tickets[type.key]} × ${type.label}`}
                                        </dt>
                                        <dd className='data-mono text-star'>
                                            {formatPrice(type.pence * tickets[type.key])}
                                        </dd>
                                    </div>
                                ))}
                                <div className='flex justify-between gap-4 border-b border-hair py-3'>
                                    <dt className='text-star-2'>
                                        Seats
                                    </dt>
                                    <dd className={cn('data-mono text-right', seats.length === needed && needed > 0 ? 'text-star' : 'text-star-3')}>
                                        {seats.length ? describeSeats(seats) : 'Not chosen yet'}
                                    </dd>
                                </div>
                                <div className='flex items-baseline justify-between gap-4 py-4'>
                                    <dt className='label-mono text-star-3'>
                                        Total
                                    </dt>
                                    <dd className='font-heading text-4xl text-star'>
                                        {formatPrice(total)}
                                    </dd>
                                </div>
                            </dl>

                            {message && (
                                <div className='mt-2'>
                                    <Notice tone={message.tone}>
                                        {message.text}
                                    </Notice>
                                </div>
                            )}

                            <button type='submit' disabled={sending} className={buttonClass('primary', 'mt-6 w-full')}>
                                {sending ? 'Booking your seats' : payment === 'card' ? `Continue to payment, ${formatPrice(total)}` : 'Confirm booking'}
                            </button>

                            <p className='mt-4 text-xs leading-relaxed text-star-3'>
                                Doors close when the show begins: please arrive fifteen minutes early. Tickets can be moved to another performance up to 24 hours before the show.
                            </p>
                        </div>
                    </aside>
                </form>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ params, query }) => {
    const id = String(params?.sessionId ?? '').slice(0, 64)
    const releasedReference = typeof query.released === 'string' ? query.released.slice(0, 20) : null

    try {
        if (releasedReference) await releaseShowHold(releasedReference, id).catch(() => undefined)

        const session = await getSession(id)

        if (!session) return { notFound: true }

        const taken = await getTakenSeats(id)
        const closed = session.status !== 'scheduled' || new Date(session.startsAt).getTime() < Date.now() + 10 * 60 * 1000

        return {
            props: {
                session,
                taken,
                cardPayments: paymentsEnabled(),
                closed,
                released: Boolean(releasedReference)
            }
        }
    }
    catch (error) {
        console.error('Booking page could not load:', error instanceof Error ? error.message : error)

        return { props: { session: null, taken: [], cardPayments: false, closed: false, released: false } }
    }
}

export default BookSession
